import _ from 'lodash'
import { length, required } from 'redux-form-validators'
import { getGpm } from '@vzmi/shp_shared_compo/helpers/proposalHelpers'
import { filterHtml } from '@vzmi/component_ec_nodejs_util/dist/lib/ValidateUtil'
import { httpHandler, safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import errorHelper from '../../../../../../lib/errorHelper'
import { RENDER_ID } from '../../../UpdateListing/helpers/converters'
import { idsOnlyParser } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'

export const FORM_SECTION = 'listing'

export const TABLE_TYPE = {
  COMPLIMENTARIES: 'complimentaries',
  SELECT_COMPLIMENTARIES: 'selectComplimentaries',
  ADDITIONAL_PURCHASES: 'additionalPurchases'
}

export const PAYLOAD_FIELD = {
  SKU: 'sku',
  MODELS: 'models',
  SELECTED_MIN_QTY: 'selectedMinQty',
  PRODUCTS: 'products',
  // models field
  ITEMS: 'items',
  SPEC: 'spec',
  // spec field
  SELECTED_VALUE: 'selectedValue'
}

export const PAYLOAD_TYPE = {
  [TABLE_TYPE.COMPLIMENTARIES]: 'complimentary',
  [TABLE_TYPE.SELECT_COMPLIMENTARIES]: 'selectComplimentary',
  [TABLE_TYPE.ADDITIONAL_PURCHASES]: 'additionalPurchases'
}

const PAYLOAD_DIFF_SUFFIX = 'Diff'
export const PAYLOAD_DIFF_TYPE = {
  [TABLE_TYPE.COMPLIMENTARIES]: `${PAYLOAD_TYPE[TABLE_TYPE.COMPLIMENTARIES]}${PAYLOAD_DIFF_SUFFIX}`,
  [TABLE_TYPE.SELECT_COMPLIMENTARIES]: `${PAYLOAD_TYPE[TABLE_TYPE.SELECT_COMPLIMENTARIES]}${PAYLOAD_DIFF_SUFFIX}`,
  [TABLE_TYPE.ADDITIONAL_PURCHASES]: `${PAYLOAD_TYPE[TABLE_TYPE.ADDITIONAL_PURCHASES]}${PAYLOAD_DIFF_SUFFIX}`
}

export const TABLE_GROUP_TYPE = {
  [TABLE_TYPE.COMPLIMENTARIES]: 'complimentaryGroups',
  [TABLE_TYPE.SELECT_COMPLIMENTARIES]: 'selectComplimentaryGroups',
  [TABLE_TYPE.ADDITIONAL_PURCHASES]: 'additionalPurchaseGroups'
}

const groupSuffix = 'MapByGroupSku'
export const TABLE_GROUP_BY_GROUP_SKU = {
  [TABLE_TYPE.COMPLIMENTARIES]: `${TABLE_GROUP_TYPE[TABLE_TYPE.COMPLIMENTARIES]}${groupSuffix}`,
  [TABLE_TYPE.SELECT_COMPLIMENTARIES]: `${TABLE_GROUP_TYPE[TABLE_TYPE.SELECT_COMPLIMENTARIES]}${groupSuffix}`,
  [TABLE_TYPE.ADDITIONAL_PURCHASES]: `${TABLE_GROUP_TYPE[TABLE_TYPE.ADDITIONAL_PURCHASES]}${groupSuffix}`
}

export const TABLE_TYPE_RELATIVE_FIELDS = {
  [TABLE_TYPE.COMPLIMENTARIES]: [
    'complimentaryGroupSkuList',
    'complimentarySkuList'
  ],
  [TABLE_TYPE.SELECT_COMPLIMENTARIES]: [
    'selectComplimentaryGroupSkyList',
    'selectComplimentarySkyList'
  ],
  [TABLE_TYPE.ADDITIONAL_PURCHASES]: [
    'additionalPurchaseSkuList'
  ]
}

export const COLUMN = {
  DRAG: 'drag',
  ID: 'sku',
  NAME: 'name',
  DISPLAY_NAME: 'spec.name',
  ATTR_NAME: 'spec.selectedValue',
  SHORT_TITLE: 'shortTitle',
  STOCK: 'stock',
  AVAILABLE_COUNT: 'availableCount',
  PART_NO: 'partNo',
  SELECT_COMPLIMENTARY_MIN_QTY: 'selectComplimentaryMinQty',
  // 在 extra model group 底下
  GROUP_NAME: 'groupName',
  // 修改狀態
  IS_NEW_IMPORT: 'isNewImport',
  IS_NEW: 'isNew',
  IS_UPDATE: 'isUpdate',
  // 加價購欄位
  COST: 'cost',
  PRICE: 'price',
  GROSS_MARGIN: 'grossMargin'
}

const FIELD_SUFFIX = 'FieldValues'

export const FORM = {
  [TABLE_TYPE.COMPLIMENTARIES]: `${TABLE_TYPE.COMPLIMENTARIES}${FIELD_SUFFIX}`,
  [TABLE_TYPE.SELECT_COMPLIMENTARIES]: `${TABLE_TYPE.SELECT_COMPLIMENTARIES}${FIELD_SUFFIX}`,
  [TABLE_TYPE.ADDITIONAL_PURCHASES]: `${TABLE_TYPE.ADDITIONAL_PURCHASES}${FIELD_SUFFIX}`
}

export const GROUP_SKU_KEY = 'groupSku'

export const COLLECT_KEY = 'collection'

export const UPDATE_COLLECT_KEY = 'isCollectUpdated'

export const requiredValidation = required({ msg: '不可為空' })

export const ATTR_NAME_LIMIT = 25

export const SHORT_TITLE_LIMIT = 10

export const getRenderId = record => _.get(record, RENDER_ID)

export const getIdsFromValue = value => {
  const isEmptyValue = _.isUndefined(value) || _.isNull(value)
  if (isEmptyValue) {
    return []
  }

  return idsOnlyParser(`${value}`)
}

export const getIdsCountFromValue = (target, isInputProps) => {
  const inputValue = isInputProps ? _.get(target, 'input.value', '') : target
  const count = _.size(getIdsFromValue(inputValue))
  return count
}

export const attrNameValidation = max => [
  requiredValidation,
  length({ max, msg: '字數超過上限' }),
  value => (filterHtml(value) !== value) ? '不可包含 HTML' : undefined
]

export const idsValidation = (max, counter) => [
  requiredValidation,
  value => (max < counter(value)) ? '編號數量超過上限' : undefined
]

export const DISPLAY_NAME_VALIDATION_TYPE = {
  NO_ATTR_INPUT: 'noAttrInput',
  ATTR_SELECT: 'attrSelect',
  SHORT_TITLE: 'shortTitle'
}

export const duplicatedInMapValidation = (tableType, renderId, inputType, message) => (value, formValues) => {
  const tableData = _.get(formValues, `${FORM_SECTION}.${tableType}`, {})
  const groupMap = _.get(formValues, `${FORM_SECTION}.${TABLE_GROUP_BY_GROUP_SKU[tableType]}`, {})
  const recordField = `${FORM[tableType]}.${renderId}`
  const record = _.get(formValues, `${FORM_SECTION}.${recordField}`, {})
  const {
    [GROUP_SKU_KEY]: groupSku
  } = record
  const isNoAttr = inputType === DISPLAY_NAME_VALIDATION_TYPE.NO_ATTR_INPUT
  let convertedValue
  switch (inputType) {
    case DISPLAY_NAME_VALIDATION_TYPE.ATTR_SELECT: {
      convertedValue = _.get(groupMap, `${value}.${COLUMN.GROUP_NAME}`, value)
      break
    }
    default: {
      convertedValue = value
    }
  }
  const map = _.flow(
    () => _.filter(tableData, current => {
      const isDiffRenderId = current[RENDER_ID] !== renderId
      if (isNoAttr) {
        return isDiffRenderId
      }

      // 選擇既有顯示名稱、既有自訂名稱
      const isDiffGroupSku = current[GROUP_SKU_KEY] !== groupSku
      return isDiffGroupSku
    }),
    filteredRecords => _.reduce(filteredRecords, (mapper, filteredRecord) => {
      const currentGroupSku = _.get(filteredRecord, GROUP_SKU_KEY)
      let mapKey
      if (_.isUndefined(currentGroupSku)) {
        mapKey = _.get(filteredRecord, COLUMN.ATTR_NAME)
      } else {
        mapKey = _.get(groupMap, `${currentGroupSku}.${COLUMN.GROUP_NAME}`, '')
      }
      mapper[mapKey] = filteredRecord
      return mapper
    }, {})
  )()
  const isDuplicated = (convertedValue in map)
  if (isDuplicated) {
    return message
  }

  return undefined
}

export const duplicatedInGroupMapValidation = (tableType, renderId, message) => (value, formValues) => {
  const tableData = _.get(formValues, `${FORM_SECTION}.${tableType}`, [])
  const group = _.groupBy(tableData, GROUP_SKU_KEY)
  const recordField = `${FORM[tableType]}.${renderId}`
  const record = _.get(formValues, `${FORM_SECTION}.${recordField}`, {})
  const {
    [GROUP_SKU_KEY]: recordGroupSku = '',
    [RENDER_ID]: recordRenderId
  } = record
  const isDuplicated = _.flow(
    () => _.get(group, recordGroupSku, []),
    groupComplimentaries => {
      const sameAttrNameComplimentary = _.find(groupComplimentaries, complimentary => {
        const specSelectValue = _.get(complimentary, COLUMN.ATTR_NAME, '')
        const renderId = _.get(complimentary, RENDER_ID, '')
        return (
          // 贈品屬性名稱一樣
          // specSelectValue === recordAttrName &&
          specSelectValue === value &&
          // 非同一列
          recordRenderId !== renderId
        )
      })
      return !_.isEmpty(sameAttrNameComplimentary)
    }
  )()
  if (isDuplicated) {
    return message
  }

  return undefined
}

export const validationChain = validates => (...args) => {
  const result = _.reduce(validates, (message, validate) => {
    if (!_.isUndefined(message)) {
      return message
    }

    message = validate(...args)
    return message
  }, undefined)
  return result
}

export const shortTitleValidationChain = validationChain([
  ...attrNameValidation(SHORT_TITLE_LIMIT)
])

export const displayNameValidationChain = (tableType, renderId, inputType) => {
  return validationChain([
    ...attrNameValidation(ATTR_NAME_LIMIT),
    duplicatedInMapValidation(tableType, renderId, inputType, '顯示名稱重複')
  ])
}

export const customDisplayNameValidationChain = validationChain([
  ...attrNameValidation(ATTR_NAME_LIMIT)
])

export const attrNameValidationChain = (tableType, renderId) => {
  return validationChain([
    ...attrNameValidation(ATTR_NAME_LIMIT),
    duplicatedInGroupMapValidation(tableType, renderId, '屬性名稱重複')
  ])
}

export const safeRequest = async (reservice, params) => {
  const [error, payload] = await safeAwait(
    reservice(params).then(httpHandler)
  )
  const errorMessage = errorHelper.parseErrorMessage(error)
  if (errorMessage) {
    return [errorMessage]
  }

  return [undefined, payload]
}

export const getSortedKey = (extraModel, groupMap) => {
  const groupSku = _.get(extraModel, GROUP_SKU_KEY)
  const isNoAttr = _.isUndefined(groupSku)
  // specs 無屬性拿 spec.selectedValue、有屬性拿 groupSku
  let sortedKey = _.get(extraModel, COLUMN.ATTR_NAME)
  if (!isNoAttr) {
    sortedKey = _.get(groupMap, `${groupSku}.${COLUMN.GROUP_NAME}`)
  }

  return sortedKey
}

// 取得不重複無屬性 spec.selectedValue、有屬性用 groupSku 找出對應 groupName
export const getSortedSpecs = (extraModels = [], groupMapByGroupSku) => {
  const specsMap = {}
  const sortedSpecs = _.reduce(extraModels, (specs, extraModel) => {
    const groupSku = _.get(extraModel, GROUP_SKU_KEY)
    const isNoAttr = _.isUndefined(groupSku)
    const spec = getSortedKey(extraModel, groupMapByGroupSku)
    const existSpec = _.get(specsMap, spec)
    const isExist = !_.isEmpty(existSpec) && !isNoAttr
    // 有屬性已存在則不再寫入 specs，無屬性不論是否存在都會寫入
    // 因為無屬性不會分群所以必須紀錄每一筆的位置才能正確排序
    if (isExist) {
      return specs
    }

    const renderId = _.get(extraModel, RENDER_ID)
    const sortedSpec = {
      [RENDER_ID]: renderId,
      isNoAttr,
      spec,
      index: _.size(specs)
    }
    specs.push(sortedSpec)
    specsMap[spec] = sortedSpec
    return specs
  }, [])
  return sortedSpecs
}

export const getIsSelectComplimentary = type => {
  const isSelectComplimentary = (type === TABLE_TYPE.SELECT_COMPLIMENTARIES)
  return isSelectComplimentary
}

// 組出贈品、加價購 api payload
export const getExtraModelsPayload = (listing = {}, shortTitleFeatureToggle) => {
  const {
    [COLUMN.SELECT_COMPLIMENTARY_MIN_QTY]: selectedMinQty
  } = listing

  const extraModelTypes = [
    TABLE_TYPE.COMPLIMENTARIES,
    TABLE_TYPE.SELECT_COMPLIMENTARIES,
    TABLE_TYPE.ADDITIONAL_PURCHASES
  ]
  const extraModels = _.reduce(extraModelTypes, (payload, extraModelType) => {
    const {
      [extraModelType]: formModels,
      [TABLE_GROUP_BY_GROUP_SKU[extraModelType]]: groupMapByGroupSku = {}
    } = listing
    const payloadType = PAYLOAD_TYPE[extraModelType]
    const isSelectComplimentary = getIsSelectComplimentary(extraModelType)
    const isAdditionalPurchases = (payloadType === PAYLOAD_TYPE[TABLE_TYPE.ADDITIONAL_PURCHASES])
    if (_.isEmpty(formModels)) {
      // 贈品任選沒有必填數量代表贈品任選是空的 payload 不列入
      if (isSelectComplimentary && _.isUndefined(selectedMinQty)) {
        return payload
      }

      if (isAdditionalPurchases) {
        payload[payloadType] = []
        return payload
      }

      // extra models 清空商品時需要帶空陣列、贈品任選清空時額外要帶必填數量 0
      payload[payloadType] = {
        [PAYLOAD_FIELD.MODELS]: [],
        ...(isSelectComplimentary && ({ [PAYLOAD_FIELD.SELECTED_MIN_QTY]: 0 }))
      }
      return payload
    }

    const sortedSpecs = getSortedSpecs(formModels, groupMapByGroupSku)
    const formModelsGroupBySpec = _.groupBy(formModels, formModel => {
      const sortedKey = getSortedKey(formModel, groupMapByGroupSku)
      return sortedKey
    })
    // 將攤平的 extra models 結構重新組成 model items
    const models = _.flow(
      () => _.map(sortedSpecs, sortedSpec => {
        const { isNoAttr, spec } = sortedSpec
        const specModels = _.get(formModelsGroupBySpec, spec, [])
        const payloadModels = _.map(specModels, specModel => {
          const modelPrice = +_.get(specModel, COLUMN.PRICE, 0)
          // 根據 shortTitleFeatureToggle 的值決定是否包含 SHORT_TITLE
          // 加價購沒有 SHORT_TITLE
          const modelFields = (
            shortTitleFeatureToggle &&
            !isAdditionalPurchases
          )
            ? [COLUMN.ID, COLUMN.ATTR_NAME, COLUMN.SHORT_TITLE]
            : [COLUMN.ID, COLUMN.ATTR_NAME]
          return {
            ..._.pick(specModel, modelFields),
            ...(isAdditionalPurchases && { [COLUMN.PRICE]: `${modelPrice}.00` })
          }
        })
        // 無屬性不變更
        if (isNoAttr) {
          return payloadModels
        }

        const groupSku = _.get(specModels, `0.${GROUP_SKU_KEY}`, '')
        const isNewGroup = (groupSku === spec)
        const attrModel = {
          // 既有有屬性需要帶 groupSku
          ...(!isNewGroup && ({ [PAYLOAD_FIELD.SKU]: groupSku })),
          [PAYLOAD_FIELD.SPEC]: { [PAYLOAD_FIELD.SELECTED_VALUE]: spec },
          // 有屬性同一個 groupSku 組到 items 裡
          [PAYLOAD_FIELD.ITEMS]: payloadModels
        }
        return [attrModel]
      }),
      payloadModelsList => _.concat(...payloadModelsList)
    )()

    if (isAdditionalPurchases) {
      payload[payloadType] = models
      return payload
    }

    payload[payloadType] = {
      ...(
        (isSelectComplimentary) && ({
          [PAYLOAD_FIELD.SELECTED_MIN_QTY]: +selectedMinQty
        })
      ),
      [PAYLOAD_FIELD.MODELS]: models
    }
    return payload
  }, {})

  return extraModels
}

// 確認、檢視頁重新組出 extra models 需要 products 才能取得所有需要的欄位
export const getExtraModelPropsFromProduct = (model = {}, productMap = {}) => {
  const {
    [PAYLOAD_FIELD.SKU]: sku
  } = model
  const product = _.get(productMap, sku, {})
  const extraModelProps = _.pick(
    product,
    [
      COLUMN.ID,
      COLUMN.NAME,
      COLUMN.STOCK,
      COLUMN.AVAILABLE_COUNT,
      COLUMN.PART_NO,
      COLUMN.COST
    ]
  )
  return extraModelProps
}

// 攤平 diff added 組成對應表
// 無屬性對應 spec.selectedValue 有屬性對應 groupSku 的 groupName + spec.selectedValue
export const getDiffAddedMap = (diff = {}) => {
  const { added: payloadModels = [] } = diff
  const addedMap = _.reduce(payloadModels, (map, payloadModel = {}) => {
    const {
      [PAYLOAD_FIELD.SKU]: groupSku,
      [PAYLOAD_FIELD.ITEMS]: items = [],
      [PAYLOAD_FIELD.SPEC]: {
        [PAYLOAD_FIELD.SELECTED_VALUE]: groupName
      } = {}
    } = payloadModel
    if (_.isEmpty(items)) {
      map[groupName] = payloadModel
      return map
    }

    for (const payloadModelItem of items) {
      const {
        [PAYLOAD_FIELD.SPEC]: {
          [PAYLOAD_FIELD.SELECTED_VALUE]: attrName
        } = {}
      } = payloadModelItem
      map[`${groupSku || groupName}${attrName}`] = payloadModelItem
    }
    return map
  }, {})
  return addedMap
}

// 確認、檢視頁組出 extra models 和有屬性分群列表
export const getExtraModelsAndGroups = (payloadExtraModels = [], products = []) => {
  const productMap = _.keyBy(products, PAYLOAD_FIELD.SKU)
  let extraModels = []
  const extraModelGroups = []
  for (const payloadExtraModel of payloadExtraModels) {
    const {
      [PAYLOAD_FIELD.SKU]: groupSku,
      [PAYLOAD_FIELD.ITEMS]: items = [],
      [PAYLOAD_FIELD.SPEC]: {
        [PAYLOAD_FIELD.SELECTED_VALUE]: groupName
      } = {}
    } = payloadExtraModel
    if (_.isEmpty(items)) {
      const extraModelProps = getExtraModelPropsFromProduct(payloadExtraModel, productMap)
      const newModel = {
        isNoAttr: true,
        ...payloadExtraModel,
        ...extraModelProps
      }
      extraModels.push(newModel)
      continue
    }

    const newExtraModelGroup = {
      // 自訂新屬性未送出提案或產生商品前不會有 groupSku，先以 groupName 取代
      [GROUP_SKU_KEY]: `${groupSku || groupName}`,
      [COLUMN.GROUP_NAME]: groupName
    }
    const newModels = _.map(items, item => {
      const extraModelProps = getExtraModelPropsFromProduct(item, productMap)
      const newModel = {
        isNoAttr: false,
        groupSku: newExtraModelGroup[GROUP_SKU_KEY],
        ...item,
        ...extraModelProps
      }
      return newModel
    })
    extraModelGroups.push(newExtraModelGroup)
    extraModels = [...extraModels, ...newModels]
  }
  return { extraModels, extraModelGroups }
}

export const getExtraModelsFromPayload = (payload, tableType) => {
  const isAdditionalPurchases = (tableType === TABLE_TYPE.ADDITIONAL_PURCHASES)
  // api 回應欄位裡加價購只有無屬性，結構上比贈品少一層 `models`
  const extraModels = isAdditionalPurchases
    ? _.get(payload, PAYLOAD_TYPE[tableType], [])
    : _.get(payload, `${PAYLOAD_TYPE[tableType]}.${[PAYLOAD_FIELD.MODELS]}`, [])
  return extraModels
}

// 確認、檢視頁組出表格需要欄位
export const getExtraModelsFieldsFromForm = (form = {}, tableType) => {
  const {
    [PAYLOAD_TYPE[tableType]]: {
      [PAYLOAD_FIELD.SELECTED_MIN_QTY]: selectedMinQty
    } = {},
    [PAYLOAD_DIFF_TYPE[tableType]]: extraModelsDiff = {},
    [PAYLOAD_FIELD.PRODUCTS]: products = []
  } = form
  const addedMap = getDiffAddedMap(extraModelsDiff)
  // api 回應欄位裡加價購只有無屬性，結構上比贈品少一層 `models`
  const payloadExtraModels = getExtraModelsFromPayload(form, tableType)
  const {
    extraModels,
    extraModelGroups
  } = getExtraModelsAndGroups(payloadExtraModels, products)
  const newExtraModels = _.map(extraModels, (extraModel, index) => {
    const {
      [GROUP_SKU_KEY]: groupSku,
      [PAYLOAD_FIELD.SPEC]: {
        [PAYLOAD_FIELD.SELECTED_VALUE]: attrName
      } = {}
    } = extraModel
    const isNoAttr = _.isUndefined(groupSku)
    const addedkey = isNoAttr ? attrName : `${groupSku}${attrName}`
    const isNew = (addedkey in addedMap)

    return {
      ...extraModel,
      [RENDER_ID]: index,
      [COLUMN.IS_NEW]: isNew
    }
  })

  const isComplimentaries = (
    tableType === TABLE_TYPE.COMPLIMENTARIES ||
    tableType === TABLE_TYPE.SELECT_COMPLIMENTARIES
  )
  const isSelectComplimentary = getIsSelectComplimentary(tableType)
  const extraModelsFields = {
    [tableType]: newExtraModels,
    [FORM[tableType]]: _.keyBy(newExtraModels, RENDER_ID),
    // 贈品有分有無屬性，有屬性時會需要分群列表
    ...(isComplimentaries && ({
      [TABLE_GROUP_TYPE[tableType]]: extraModelGroups,
      [TABLE_GROUP_BY_GROUP_SKU[tableType]]: _.keyBy(extraModelGroups, GROUP_SKU_KEY)
    })),
    // 贈品任選需要必選數量
    ...(isSelectComplimentary && {
      [COLUMN.SELECT_COMPLIMENTARY_MIN_QTY]: selectedMinQty
    })
  }
  return extraModelsFields
}

// 確認、檢視頁組出移除的表格資料
export const getRemovedExtraModelsFromForm = (form = {}, tableType) => {
  const {
    [PAYLOAD_DIFF_TYPE[tableType]]: {
      removed: payloadExtraModels = []
    } = {},
    [PAYLOAD_FIELD.PRODUCTS]: products = []
  } = form
  const {
    extraModels,
    extraModelGroups
  } = getExtraModelsAndGroups(payloadExtraModels, products)
  const groupMap = _.keyBy(extraModelGroups, GROUP_SKU_KEY)
  const data = _.map(extraModels, (extraModel, index) => {
    const {
      [GROUP_SKU_KEY]: groupSku
    } = extraModel
    const isNoAttr = _.isUndefined(groupSku)
    if (isNoAttr) {
      return extraModel
    }

    const groupName = _.get(groupMap, `${groupSku}.${COLUMN.GROUP_NAME}`, '')
    return {
      ...extraModel,
      index,
      isNoAttr,
      [COLUMN.GROUP_NAME]: groupName
    }
  })
  return data
}

export const getGroupFirstIndex = (data, groupSku) => {
  const groupFirstIndex = _.reduce(data, (groupIndex, item, index) => {
    if (groupIndex !== -1) {
      return groupIndex
    }

    const itemGroupSku = _.get(item, GROUP_SKU_KEY)
    if (`${groupSku}` !== `${itemGroupSku}`) {
      return groupIndex
    }

    groupIndex = index
    return groupIndex
  }, -1)
  return groupFirstIndex
}

export const LIMIT = {
  GROUP: 99,
  RECORDS: 20
}
export const getExtraModelsOverLimitInfo = (modelsGroupByGroupSku = {}, groupMapByGroupSku) => {
  const {
    // 無屬性都會分到 undefined
    undefined: noAttrRecords = [],
    // 有屬性分群
    ...attrModelsGroupByGroupSku
  } = modelsGroupByGroupSku
  const totalGroupSize = (
    _.size(noAttrRecords) +
    _.size(_.keys(attrModelsGroupByGroupSku))
  )
  if (totalGroupSize > LIMIT.GROUP) {
    return {
      isOverLimit: true,
      errorMessage: `最多 ${LIMIT.GROUP} 種顯示名稱`
    }
  }

  const overlimitAttrNames = _.reduce(attrModelsGroupByGroupSku, (attrNames, records, groupSku) => {
    if (_.size(records) <= LIMIT.RECORDS) {
      return attrNames
    }

    const groupName = _.get(groupMapByGroupSku, `${groupSku}.${COLUMN.GROUP_NAME}`)
    attrNames.push(groupName)
    return attrNames
  }, [])
  if (!_.isEmpty(overlimitAttrNames)) {
    return {
      isOverLimit: true,
      errorMessage: `${_.join(overlimitAttrNames)} 最多 ${LIMIT.RECORDS} 組屬性名稱`
    }
  }

  return { isOverLimit: false }
}

export const getGrossMargin = (price, cost) => {
  if (price && !(_.isNaN(price) || _.isNaN(cost))) {
    return _.floor(getGpm(`${price}`, `${cost}`) * 100, 2)
  }

  return 0
}
